import {
  usePassengersAlternateIdentifiersListQuery,
  usePassengersCreateMutation,
  usePassengersDestroyMutation,
  usePassengersPartialUpdateMutation,
  useTilledPaymentMethodCreateMutation,
} from '@bambi/api-client/bambi';
import type { Passenger } from '@bambi/api-client/schemas';
import { ContextFormFieldText } from '@bambi/component-library/src/components/forms/context/ContextFormFieldText';
import { FormErrorMessage } from '@bambi/component-library/src/components/forms/FormErrorMessage/FormErrorMessage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { DeleteModal } from './DeleteModal';
import type { FormValues } from './FormValues';
import { PassengerBillingForm } from './PassengerBillingForm';
import { PassengerForm } from './PassengerForm';
import { PassengerFormActions } from './PassengerFormActions';
import { PayerPassengerDataGrid } from './payer-passenger-datagrid/PayerPassengerDataGrid';
import { PreferredDriverDataGrid } from './preferred-driver-datagrid/PreferredDriverDataGrid';
import { useManageAlternateIdentifier } from './useManageAlternateIdentifier';
import { useAppDispatch } from 'src/app/store/hooks';
import { usePaymentMethodCaptureContext } from 'src/common/forms/PaymentMethodCaptureForm/PaymentMethodCaptureContext';
import type { BillingDetails } from 'src/common/forms/PaymentMethodCaptureForm/PaymentMethodCaptureForm';
import { validateBillingAddress } from 'src/common/forms/PaymentMethodCaptureForm/PaymentMethodCaptureForm';
import { useTilled } from 'src/common/forms/PaymentMethodCaptureForm/useTilled';
import type { OpenHandler } from 'src/common/modals/RequiresConfirmationModal/RequiresConfirmationModal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import { formatFullName } from 'src/common/util/formatFullName';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { selectPassenger } from 'src/features/add-trip/addATrip.slice';
import { useUserRoles } from 'src/features/auth/useUserRoles';
import { useIsRideOrderingPortalEnabledForOrganization } from 'src/features/rideOrderingPortal/useIsRideOrderingPortalEnabledForOrganization';

/**
 * Controls the create/update operations of the passenger. There is enough logic
 * in here that doesn't exactly fit into either the dialog or the form.
 */
interface PassengerFormControllerProps {
  passenger?: Passenger;
  setOpen: OpenHandler;
}
export function PassengerFormController({
  passenger,
  setOpen,
}: PassengerFormControllerProps) {
  const dispatch = useAppDispatch();
  const {
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting },
  } = useFormContext<FormValues>();
  const billingForm = useForm<{
    billing_details: BillingDetails;
  }>({
    defaultValues: {
      billing_details: {
        billingName: passenger?.full_name || '',
      },
    },
  });
  const {
    getValues: getValuesBilling,
    setValue: setValueBilling,
    reset: resetBilling,
  } = billingForm;
  const alternateIdentifierForm = useForm<{ alternate_identifier: string }>({
    defaultValues: {
      alternate_identifier: '',
    },
  });

  const {
    getValues: getValuesAlternateIdentifier,
    setValue: setValueAlternateIdentifier,
    reset: resetAlternateIdentifier,
  } = alternateIdentifierForm;

  const [serverError, setServerError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const [createPassenger] = usePassengersCreateMutation();
  const [updatePassenger] = usePassengersPartialUpdateMutation();
  const [deletePassenger, { isLoading: isDeletingPassenger }] =
    usePassengersDestroyMutation();
  const { savePaymentMethod, validateForm: validatePaymentForm } =
    useTilled('card');
  const [savePaymentMethodCreate] = useTilledPaymentMethodCreateMutation();
  const { areFieldsEmpty } = usePaymentMethodCaptureContext();

  const isRideOrderingPortalEnabledForOrganization =
    useIsRideOrderingPortalEnabledForOrganization();

  const isEditing = !!passenger?.id;

  const { isAdmin, isDispatcher, isBambi, isOwner } = useUserRoles();
  const canManageDriverPreferences =
    isAdmin || isDispatcher || isBambi || isOwner;

  const { data: alternateIdentifiers } =
    usePassengersAlternateIdentifiersListQuery(
      { passengerId: passenger?.id ?? '' },
      {
        skip: !isEditing,
        refetchOnMountOrArgChange: true,
      }
    );

  const isLoading = useMemo(
    () => isSubmitting || isDeletingPassenger,
    [isSubmitting, isDeletingPassenger]
  );

  // Update form state manually. Default values only apply when the component is
  // first mounted, and this dialog may remain mounted across the app due to the
  // trip form tie in.
  useEffect(() => {
    setIsConfirmingDelete(false);
    if (passenger) {
      setValue('first_name', passenger.first_name);
      setValue('last_name', passenger.last_name);
      setValue('dob', passenger.dob || '');
      setValue('gender', passenger.gender || '');
      setValue('weight', passenger.weight || 0);
      setValue('is_student', passenger.is_student || false);
      setValue(
        'needs_bariatric_transport',
        passenger.needs_bariatric_transport || false
      );
      setValue('phone_number', passenger.phone_number || '');
      setValue('email', passenger.email || '');
      setValue('notes', passenger.notes);
      setValue('dispatcher_notes', passenger.dispatcher_notes);
      setValue('payer_ids', passenger.payer_ids || []);
      setValue(
        'tags',
        passenger.tags.map((passengerTag) => ({
          label: passengerTag.tag_name,
          value: passengerTag.tag,
          color: passengerTag.color_hex,
        }))
      );
      setValue('allow_sms_message', passenger.allow_sms_message ?? false);
      setValue('pronoun', passenger.pronoun || '');
      setValue('languages', passenger.languages || []);
      setValue('primary_language', passenger.primary_language || '');
      setValue('preferred_name', passenger.preferred_name || '');
      setValueBilling('billing_details', {
        city: '',
        country: '',
        zip: '',
        state: '',
        street: '',
        street2: '',
        billingName: passenger.full_name || '',
      });
      setValue(
        'allow_trip_confirmation',
        passenger.allow_trip_confirmation ?? true
      );
    } else {
      reset();
      resetBilling();
      resetAlternateIdentifier();
    }
  }, [
    passenger,
    setValue,
    reset,
    setValueBilling,
    resetBilling,
    setIsConfirmingDelete,
    resetAlternateIdentifier,
  ]);

  useEffect(() => {
    if (alternateIdentifiers && alternateIdentifiers.results.length > 0) {
      setValueAlternateIdentifier(
        'alternate_identifier',
        alternateIdentifiers.results[0].identifier
      );
    }
  }, [alternateIdentifiers, setValueAlternateIdentifier]);

  const handleBillingUpsert = useCallback(
    async (passenger: Passenger) => {
      // Skip saving payment method
      if (areFieldsEmpty()) {
        return;
      }

      const data = getValuesBilling();
      const billingDetails = data.billing_details;
      const billingName = data.billing_details.billingName;
      const billingAddress = {
        city: billingDetails.city,
        country: billingDetails.country,
        zip: billingDetails.zip,
        state: billingDetails.state,
        street: billingDetails.street,
        street2: billingDetails.street2,
      };

      const validPaymentForm = await validatePaymentForm();
      const validatedBillingAddress = validateBillingAddress(billingAddress);
      const validBillingName = Boolean(billingName.replace(/\s+/g, '').length);
      const billingNameToUse = validBillingName
        ? billingName
        : `${passenger.first_name} ${passenger.last_name}`;
      const validBillingNameToUse = Boolean(
        billingNameToUse.replace(/\s+/g, '').length
      );

      // Payment method form is invalid
      if (!validPaymentForm || !validBillingNameToUse) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to save payment method',
            description: 'Payment details are invalid, or missing',
          })
        );
        return;
      }
      // Payment method form is valid, attempt to save
      try {
        // Persist payment method in tilled
        const savedPaymentMethod = await savePaymentMethod({
          type: 'card',
          billing_details: {
            name: billingNameToUse,
            address: validatedBillingAddress
              ? {
                  ...validatedBillingAddress,
                  zip: validatedBillingAddress.zip,
                }
              : undefined,
          },
        });

        // Attach payment method to pax
        await savePaymentMethodCreate({
          tilledPaymentMethodInput: {
            payer_id: passenger.id,
            payer_type: 'passenger',
            payment_method_id: savedPaymentMethod.id,
          },
        });
        dispatch(
          show({
            title: 'Saved payment method',
            type: 'success',
          })
        );
      } catch (error) {
        const formattedErr = formatServerError(error);
        setServerError(formattedErr);
        dispatch(
          show({
            type: 'error',
            title: 'Failed to save payment method',
          })
        );
      }
    },
    [
      dispatch,
      savePaymentMethod,
      savePaymentMethodCreate,
      validatePaymentForm,
      areFieldsEmpty,
      getValuesBilling,
    ]
  );

  const managedAlternateIdentifier = alternateIdentifiers?.results[0];

  const handleManageAlternateIdentifier = useManageAlternateIdentifier();
  const handleAlternateIdentifierUpsert = useCallback(
    async (passenger: Passenger) => {
      const data = getValuesAlternateIdentifier();

      try {
        await handleManageAlternateIdentifier({
          alternateIdentifier: data.alternate_identifier,
          alternateIdentifierId: managedAlternateIdentifier?.id,
          passengerId: passenger.id,
        });
      } catch (e) {
        dispatch(
          show({
            title: 'Failed to update EMR number',
            description: formatServerError(e),
            type: 'error',
          })
        );
      }
    },
    [
      handleManageAlternateIdentifier,
      getValuesAlternateIdentifier,
      managedAlternateIdentifier,
      dispatch,
    ]
  );

  const handlePassengerUpsert = useCallback<SubmitHandler<FormValues>>(
    async (data) => {
      try {
        setServerError(null);

        // Do not send these values that are null or empty strings
        if (!data.dob) delete data.dob;
        if (!data.gender) delete data.gender;
        if (!isRideOrderingPortalEnabledForOrganization) delete data.payer_ids;

        // Currently, we only add passengers through the trip form, so this is only needed when editing
        // There's some backend work on the /passengers/ POST endpoint to accept payer_ids. Hiding this
        // lets us punt that til we want to add passenger creation to the passenger list page
        // Additional context: https://bambi-health.slack.com/archives/C03M253G7RV/p1735595057713669
        if (!isEditing) {
          delete data.payer_ids;
        }

        // the backend expects tag_ids, so we need to convert the tags to tag_ids
        // and remove the tags from the data object
        const tagIds = data.tags?.map((tag) => tag.value);
        delete data.tags;

        const upsertedPassenger = isEditing
          ? await updatePassenger({
              id: passenger.id!,
              patchedPassengerWritable: {
                ...data,
                phone_number:
                  typeof data.phone_number === 'undefined'
                    ? ''
                    : data.phone_number,
                tag_ids: tagIds,
              },
            }).unwrap()
          : await createPassenger({
              passengerWritable: {
                ...data,
                tag_ids: tagIds,
              },
            }).unwrap();

        // TODO: It would be better to move this state into a Passenger store and
        // have the trip form read from that instead of dispatching to the addATrip slice
        dispatch(selectPassenger(upsertedPassenger));
        if (isEditing) {
          dispatch(
            show({
              type: 'success',
              title: 'Passenger updated successfully',
              description: `${data.first_name} has been updated`,
            })
          );
        } else {
          dispatch(
            show({
              type: 'success',
              title: 'Passenger created successfully',
              description: `${data.first_name} has been created`,
            })
          );
        }

        // Potentially update the billing info
        await handleBillingUpsert(upsertedPassenger);

        // Update the alternate identifier record (handled separately because it's
        // separate from the passenger, and could later evolve into a table)
        await handleAlternateIdentifierUpsert(upsertedPassenger);

        // Close the modal
        setOpen(false, true);
      } catch (error) {
        const formattedErr = formatServerError(error);
        setServerError(formattedErr);
        dispatch(
          show({
            type: 'error',
            title: 'Failed to create passenger',
            description: formattedErr,
          })
        );
      }
    },
    [
      setServerError,
      createPassenger,
      updatePassenger,
      dispatch,
      isRideOrderingPortalEnabledForOrganization,
      isEditing,
      handleBillingUpsert,
      setOpen,
      passenger,
      handleAlternateIdentifierUpsert,
    ]
  );

  const handlePassengerDelete = useCallback(async () => {
    try {
      await deletePassenger({ id: passenger?.id || '' }).unwrap();
      setIsConfirmingDelete(false);
      setOpen(false);
      reset();
      dispatch(
        show({
          type: 'success',
          title: 'Successfully deleted passenger',
        })
      );
    } catch (e) {
      const formattedErr = formatServerError(e);
      setDeleteError(formattedErr);
      dispatch(
        show({
          type: 'error',
          title: 'Failed to delete passenger',
          description: formattedErr,
        })
      );
    }
  }, [
    deletePassenger,
    setIsConfirmingDelete,
    setOpen,
    reset,
    dispatch,
    setDeleteError,
    passenger,
  ]);

  return (
    <div>
      <div>
        <h3 className="text-[24px] font-medium leading-6 text-gray-900">
          {isEditing ? 'Edit Passenger' : 'Add a New Passenger'}
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          {isEditing ? 'Edit' : 'Create a'} profile for the new passenger. You
          will be able to edit these details whenever after you save it.
        </p>
        <p className="mt-2 text-xs text-gray-500">*Required</p>
      </div>
      {serverError ? <FormErrorMessage>{serverError}</FormErrorMessage> : null}
      <PassengerForm passengerId={passenger?.id} />
      <div className="mt-4">
        <FormProvider {...alternateIdentifierForm}>
          <ContextFormFieldText
            fieldPath="alternate_identifier"
            label="Electronic Medical Record Number"
            placeholder="EMR Number"
          />
        </FormProvider>
      </div>
      {isEditing ? (
        <>
          <PayerPassengerDataGrid
            payerPassengerIds={passenger.payer_ids || []}
            passengerId={passenger.id || ''}
          />
        </>
      ) : null}
      {isEditing && canManageDriverPreferences && (
        <>
          <hr className="mb-4 mt-6 border-gray-200" />
          <PreferredDriverDataGrid
            passengerId={passenger.id!}
            passengerName={formatFullName(passenger)}
          />
        </>
      )}
      <hr className="mb-4 mt-6 border-gray-200" />
      <FormProvider {...billingForm}>
        <PassengerBillingForm passengerId={passenger?.id ?? ''} />
      </FormProvider>
      <div className="sticky bottom-[-1rem] z-[100] border-t-2 border-gray-200 bg-white py-2">
        <PassengerFormActions
          isEditing={isEditing}
          isLoading={isLoading}
          onDelete={() => setIsConfirmingDelete(true)}
          onCancel={() => setOpen(false)}
          onSave={handleSubmit(handlePassengerUpsert)}
        />
      </div>
      <DeleteModal
        open={isConfirmingDelete}
        setOpen={setIsConfirmingDelete}
        onCancel={() => {
          setOpen(true);
          setIsConfirmingDelete(false);
        }}
        onConfirm={handlePassengerDelete}
        error={deleteError}
      />
    </div>
  );
}
